import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  Home, MapPin, User, Plus, Search, Edit3, Trash2, Save, X, 
  FileText, Calendar, ArrowRight, Building2, Phone, Mail
} from 'lucide-react';
import Button from './ui/Button';

const Properties = ({ user, activeCompanyId }) => {
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clientFolders, setClientFolders] = useState([]); // Dossiers clients créés manuellement
  const [view, setView] = useState('list'); // 'list', 'createFolder', 'clientDetail', 'propertyDetail'
  const [activeFolder, setActiveFolder] = useState(null);
  const [activeProperty, setActiveProperty] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    
    // Charger les biens
    const propertiesQuery = query(
      collection(db, 'companies', activeCompanyId, 'properties'), 
      orderBy('createdAt', 'desc')
    );
    const unsubProperties = onSnapshot(propertiesQuery, (snapshot) => {
      setProperties(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Charger les clients
    const unsubClients = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'clients'),
      (snapshot) => {
        setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    // Charger les prospects
    const unsubProspects = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'prospects'),
      (snapshot) => {
        setProspects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    // Charger les devis
    const unsubQuotes = onSnapshot(
      collection(db, 'companies', activeCompanyId, 'quotes'),
      (snapshot) => {
        setQuotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    // Charger les dossiers clients (collection pour gérer plusieurs biens)
    const unsubFolders = onSnapshot(
      query(collection(db, 'companies', activeCompanyId, 'clientFolders'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        setClientFolders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    return () => {
      unsubProperties();
      unsubClients();
      unsubProspects();
      unsubQuotes();
      unsubFolders();
    };
  }, [user, activeCompanyId]);

  const filteredFolders = clientFolders.filter(f => 
    f.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectProperty = (property) => {
    setActiveProperty(property);
    setView('propertyDetail');
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 animate-page pb-24">
      {view === 'list' && (
        <ClientFoldersList 
          folders={filteredFolders}
          properties={properties}
          prospects={prospects}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onNew={() => setView('createFolder')}
          onSelectFolder={(folder) => { setActiveFolder(folder); setView('clientDetail'); }}
        />
      )}

      {view === 'propertyDetail' && activeProperty && (
        <PropertyDetailView 
          property={activeProperty}
          prospects={prospects.filter(p => p.propertyId === activeProperty.id)}
          quotes={quotes.filter(q => q.propertyId === activeProperty.id)}
          activeCompanyId={activeCompanyId}
          onBack={() => {
            setActiveProperty(null);
            setView('clientDetail');
          }}
          onCreateQuote={(propertyData) => {
            window.dispatchEvent(new CustomEvent('createFromProperty', { detail: propertyData }));
          }}
        />
      )}

      {view === 'createFolder' && (
        <CreateClientFolder
          clients={clients}
          activeCompanyId={activeCompanyId}
          onCancel={() => setView('list')}
          onSuccess={() => setView('list')}
        />
      )}
      
      {view === 'clientDetail' && activeFolder && (
        <ClientDetailView 
          folder={activeFolder}
          properties={properties.filter(p => p.folderId === activeFolder.id)}
          prospects={prospects.filter(p => p.clientId === activeFolder.clientId)}
          quotes={quotes}
          activeCompanyId={activeCompanyId}
          onBack={() => { setActiveFolder(null); setView('list'); }}
          onSelectProperty={handleSelectProperty}
        />
      )}
    </div>
  );
};

// --- LISTE DES DOSSIERS CLIENTS ---
const ClientFoldersList = ({ folders, properties, prospects, searchTerm, setSearchTerm, onNew, onSelectFolder }) => {
  const stats = {
    totalFolders: folders.length,
    totalProperties: properties.filter(p => p.folderId).length,
  };

  return (
    <div className="animate-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Building2 className="text-indigo-600" size={32}/>
            Biens Multi-Propriétés
          </h1>
          <p className="text-slate-500 text-sm mt-1">Gérez les clients avec plusieurs biens</p>
        </div>
        <Button variant="primary" onClick={onNew} icon={Plus}>
          Nouveau Dossier Client
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
          <div className="text-2xl font-bold text-indigo-600">{stats.totalFolders}</div>
          <div className="text-xs text-indigo-600 font-medium">Dossiers clients</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
          <div className="text-2xl font-bold text-blue-600">{stats.totalProperties}</div>
          <div className="text-xs text-blue-600 font-medium">Biens gérés</div>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
        <input 
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un dossier client..."
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Liste */}
      {folders.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200">
          <Building2 size={48} className="mx-auto text-slate-300 mb-4"/>
          <p className="text-slate-500 font-medium">Aucun dossier client créé</p>
          <p className="text-slate-400 text-sm mt-2">Créez un dossier pour gérer les clients avec plusieurs biens</p>
        </div>
      ) : (
        <div className="space-y-3">
          {folders.map(folder => {
            const folderProperties = properties.filter(p => p.folderId === folder.id);
            const folderProspects = prospects.filter(p => p.clientId === folder.clientId);
            
            return (
              <button
                key={folder.id}
                onClick={() => onSelectFolder(folder)}
                className="w-full bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition text-left group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <User size={20} className="text-indigo-600"/>
                      <h3 className="font-bold text-slate-800 text-lg group-hover:text-indigo-600 transition">
                        {folder.clientName}
                      </h3>
                    </div>
                    {folder.notes && (
                      <p className="text-sm text-slate-600 mb-2">{folder.notes}</p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      {folderProperties.length > 0 && (
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Home size={12}/>
                          {folderProperties.length} bien{folderProperties.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {folderProspects.length > 0 && (
                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                          {folderProspects.length} projet{folderProspects.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-indigo-600 transition"/>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// --- FORMULAIRE CRÉATION DOSSIER CLIENT ---
const CreateClientFolder = ({ clients, activeCompanyId, onCancel, onSuccess }) => {
  const [form, setForm] = useState({
    clientId: '',
    clientName: '',
    notes: ''
  });

  const handleSelectClient = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm({ ...form, clientId: client.id, clientName: client.name });
    }
  };

  const handleSubmit = async () => {
    if (!form.clientId) {
      alert('Veuillez sélectionner un client');
      return;
    }

    try {
      await addDoc(collection(db, 'companies', activeCompanyId, 'clientFolders'), {
        ...form,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      onSuccess();
    } catch (error) {
      console.error('Erreur création dossier:', error);
      alert('Erreur lors de la création');
    }
  };

  return (
    <div className="animate-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <Building2 className="text-indigo-600" size={28}/>
          Nouveau Dossier Client
        </h2>

        <div className="space-y-4">
          {/* Sélection client */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <User size={16} className="text-indigo-600"/>
              Sélectionner le client *
            </label>
            
            {clients.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Créez d'abord un client dans l'onglet Clients.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {clients.map(client => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelectClient(client.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                      form.clientId === client.id 
                        ? 'border-indigo-500 bg-indigo-100 text-indigo-900 font-medium' 
                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
                    }`}
                  >
                    <div className="font-medium">{client.name}</div>
                    {client.phone && <div className="text-xs text-slate-500 mt-1">{client.phone}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Informations sur ce dossier client..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onCancel} icon={X} className="flex-1">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} icon={Save} className="flex-1">
            Créer le dossier
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- ANCIENNE LISTE (supprimée) ---
const OldClientPropertiesList = ({ clients, properties, prospects, quotes, searchTerm, setSearchTerm, onSelectClient }) => {
  // Calculer les stats par client
  const clientsWithStats = clients.map(client => {
    const clientProperties = properties.filter(p => p.clientId === client.id);
    const clientProspects = prospects.filter(p => p.clientId === client.id);
    const clientQuotes = quotes.filter(q => {
      // Un devis peut être lié soit directement au client, soit via un prospect
      return clientProspects.some(prospect => prospect.id === q.prospectId);
    });
    
    return {
      ...client,
      propertiesCount: clientProperties.length,
      prospectsCount: clientProspects.length,
      quotesCount: clientQuotes.length
    };
  });

  const stats = {
    totalClients: clients.length,
    withProperties: clientsWithStats.filter(c => c.propertiesCount > 0).length,
    totalProperties: properties.length,
  };

  return (
    <div className="animate-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Building2 className="text-indigo-600" size={32}/>
            Biens par Client
          </h1>
          <p className="text-slate-500 text-sm mt-1">Vue d'ensemble des biens et projets de vos clients</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
          <div className="text-2xl font-bold text-indigo-600">{stats.totalClients}</div>
          <div className="text-xs text-indigo-600 font-medium">Clients</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
          <div className="text-2xl font-bold text-blue-600">{stats.totalProperties}</div>
          <div className="text-xs text-blue-600 font-medium">Biens</div>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-100">
          <div className="text-2xl font-bold text-green-600">{stats.withProperties}</div>
          <div className="text-xs text-green-600 font-medium">Avec biens</div>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
        <input 
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un client..."
          className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Liste des clients */}
      {clientsWithStats.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200">
          <User size={48} className="mx-auto text-slate-300 mb-4"/>
          <p className="text-slate-500 font-medium">Aucun client trouvé</p>
          <p className="text-slate-400 text-sm mt-2">Créez vos clients dans l'onglet Clients</p>
        </div>
      ) : (
        <div className="space-y-3">
          {clientsWithStats.map(client => (
            <button
              key={client.id}
              onClick={() => onSelectClient(client)}
              className="w-full bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition text-left group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User size={20} className="text-indigo-600"/>
                    <h3 className="font-bold text-slate-800 text-lg group-hover:text-indigo-600 transition">
                      {client.name}
                    </h3>
                  </div>
                  <div className="text-sm text-slate-600 space-y-1">
                    {client.phone && (
                      <p className="flex items-center gap-2">
                        <Phone size={14}/>
                        {client.phone}
                      </p>
                    )}
                    {client.email && (
                      <p className="flex items-center gap-2">
                        <Mail size={14}/>
                        {client.email}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    {client.propertiesCount > 0 && (
                      <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Home size={12}/>
                        {client.propertiesCount} bien{client.propertiesCount > 1 ? 's' : ''}
                      </span>
                    )}
                    {client.quotesCount > 0 && (
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <FileText size={12}/>
                        {client.quotesCount}
                      </span>
                    )}
                  </div>
                  <ArrowRight size={18} className="text-slate-400 group-hover:text-indigo-600 transition"/>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- FORMULAIRE NOUVEAU BIEN ---
const NewPropertyForm = ({ activeCompanyId, clients, onCancel, onSuccess }) => {
  const [form, setForm] = useState({
    address: '',
    postalCode: '',
    city: '',
    clientId: '',
    clientName: '',
    notes: '',
    type: 'maison' // maison, appartement, local, terrain
  });

  const handleSelectClient = (clientId) => {
    if (!clientId) {
      setForm(prev => ({ ...prev, clientId: '', clientName: '' }));
      return;
    }
    
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setForm(prev => ({
        ...prev,
        clientId: client.id,
        clientName: client.name
      }));
    }
  };

  const handleSubmit = async () => {
    if (!form.address || !form.city || !form.clientId) {
      alert('Adresse, ville et client sont obligatoires');
      return;
    }

    try {
      await addDoc(collection(db, 'companies', activeCompanyId, 'properties'), {
        ...form,
        projectsCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      onSuccess();
    } catch (error) {
      console.error('Erreur création bien:', error);
      alert('Erreur lors de la création');
    }
  };

  return (
    <div className="animate-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
          <Building2 className="text-indigo-600" size={28}/>
          Nouveau Bien
        </h2>

        <div className="space-y-4">
          {/* Sélection client */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <label className="block text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <User size={16} className="text-indigo-600"/>
              Propriétaire *
            </label>
            
            {clients.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Créez d'abord un client dans l'onglet Clients.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {clients.map(client => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelectClient(client.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition ${
                      form.clientId === client.id 
                        ? 'border-indigo-500 bg-indigo-100 text-indigo-900 font-medium' 
                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
                    }`}
                  >
                    <div className="font-medium">{client.name}</div>
                    {client.phone && <div className="text-xs text-slate-500 mt-1">{client.phone}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Type de bien */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Type de bien
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value})}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="maison">🏠 Maison</option>
              <option value="appartement">🏢 Appartement</option>
              <option value="immeuble">🏬 Immeuble</option>
              <option value="local">🏪 Local commercial</option>
              <option value="terrain">🌳 Terrain</option>
            </select>
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Adresse du bien *
            </label>
            <input 
              type="text"
              value={form.address}
              onChange={(e) => setForm({...form, address: e.target.value})}
              placeholder="12 rue de la Paix"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Code postal et Ville */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Code postal *
              </label>
              <input 
                type="text"
                value={form.postalCode}
                onChange={(e) => setForm({...form, postalCode: e.target.value})}
                placeholder="75001"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Ville *
              </label>
              <input 
                type="text"
                value={form.city}
                onChange={(e) => setForm({...form, city: e.target.value})}
                placeholder="Paris"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2">
              Notes / Description
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Informations complémentaires sur le bien..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onCancel} icon={X} className="flex-1">
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSubmit} icon={Save} className="flex-1">
            Créer le bien
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- VUE DÉTAIL DOSSIER CLIENT AVEC SES BIENS ---
const ClientDetailView = ({ folder, properties, prospects, quotes, activeCompanyId, onBack, onSelectProperty }) => {
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newPropertyForm, setNewPropertyForm] = useState({
    address: '',
    postalCode: '',
    city: '',
    type: 'maison',
    notes: ''
  });

  const handleAddProperty = async () => {
    if (!newPropertyForm.address || !newPropertyForm.city) {
      alert('Adresse et ville sont obligatoires');
      return;
    }

    try {
      await addDoc(collection(db, 'companies', activeCompanyId, 'properties'), {
        ...newPropertyForm,
        folderId: folder.id,
        clientId: folder.clientId,
        clientName: folder.clientName,
        projectsCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setShowAddProperty(false);
      setNewPropertyForm({ address: '', postalCode: '', city: '', type: 'maison', notes: '' });
    } catch (error) {
      console.error('Erreur ajout bien:', error);
      alert('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Supprimer ce bien ?')) return;
    try {
      await deleteDoc(doc(db, 'companies', activeCompanyId, 'properties', propertyId));
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const typeEmoji = {
    maison: '🏠',
    appartement: '🏢',
    immeuble: '🏬',
    local: '🏪',
    terrain: '🌳'
  };

  return (
    <div className="animate-page">
      {/* Header Client */}
      <div className="relative bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 mb-6 overflow-hidden">
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm -z-10"></div>
        <div className="text-white relative z-10">
          <button 
            onClick={onBack}
            className="text-indigo-100 hover:text-white flex items-center gap-2 text-sm font-medium mb-4 transition"
          >
            <ArrowRight size={16} className="rotate-180"/> Retour à la liste
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-3 flex items-center gap-3">
                <User size={32}/>
                {folder.clientName}
              </h1>
              {folder.notes && (
                <p className="text-indigo-100 text-sm">{folder.notes}</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <div className="bg-white/20 px-4 py-2 rounded-lg text-center">
                <div className="text-2xl font-bold">{properties.length}</div>
                <div className="text-xs">Bien{properties.length > 1 ? 's' : ''}</div>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-lg text-center">
                <div className="text-2xl font-bold">{prospects.length}</div>
                <div className="text-xs">Prospect{prospects.length > 1 ? 's' : ''}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des biens */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Building2 size={20} className="text-indigo-600"/>
            Biens de {folder.clientName}
          </h3>
          <Button variant="primary" onClick={() => setShowAddProperty(!showAddProperty)} icon={Plus} className="text-sm">
            Ajouter un bien
          </Button>
        </div>

        {showAddProperty && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={newPropertyForm.type}
                  onChange={(e) => setNewPropertyForm({...newPropertyForm, type: e.target.value})}
                  className="px-3 py-2 border border-indigo-300 rounded-lg bg-white text-sm"
                >
                  <option value="maison">🏠 Maison</option>
                  <option value="appartement">🏢 Appartement</option>
                  <option value="immeuble">🏬 Immeuble</option>
                  <option value="local">🏪 Local</option>
                  <option value="terrain">🌳 Terrain</option>
                </select>
              </div>
              <input 
                type="text"
                value={newPropertyForm.address}
                onChange={(e) => setNewPropertyForm({...newPropertyForm, address: e.target.value})}
                placeholder="Adresse"
                className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <input 
                  type="text"
                  value={newPropertyForm.postalCode}
                  onChange={(e) => setNewPropertyForm({...newPropertyForm, postalCode: e.target.value})}
                  placeholder="Code postal"
                  className="px-3 py-2 border border-indigo-300 rounded-lg text-sm"
                />
                <input 
                  type="text"
                  value={newPropertyForm.city}
                  onChange={(e) => setNewPropertyForm({...newPropertyForm, city: e.target.value})}
                  placeholder="Ville"
                  className="px-3 py-2 border border-indigo-300 rounded-lg text-sm"
                />
              </div>
              <textarea
                rows={2}
                value={newPropertyForm.notes}
                onChange={(e) => setNewPropertyForm({...newPropertyForm, notes: e.target.value})}
                placeholder="Notes..."
                className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm resize-none"
              />
              <div className="flex gap-2">
                <Button variant="primary" onClick={handleAddProperty} icon={Save} className="flex-1 text-sm">
                  Enregistrer
                </Button>
                <button
                  onClick={() => setShowAddProperty(false)}
                  className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 transition"
                >
                  <X size={16}/>
                </button>
              </div>
            </div>
          </div>
        )}

        {properties.length === 0 ? (
          <p className="text-slate-500 text-sm italic text-center py-4">Aucun bien enregistré pour ce client.</p>
        ) : (
          <div className="space-y-3">
            {properties.map(property => {
              const propertyProspects = prospects.filter(p => p.propertyId === property.id);
              const propertyQuotes = quotes.filter(q => propertyProspects.some(pr => pr.id === q.prospectId));
              
              return (
                <div key={property.id} className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-all group">
                  <button
                    onClick={() => onSelectProperty(property)}
                    className="w-full text-left p-4 hover:bg-slate-50 transition"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{typeEmoji[property.type] || '🏠'}</span>
                          <h4 className="font-bold text-slate-800 group-hover:text-indigo-600 transition">{property.address}</h4>
                          <ArrowRight size={16} className="text-slate-400 group-hover:text-indigo-600 transition"/>
                        </div>
                        <p className="text-sm text-slate-600">
                          {property.postalCode} {property.city}
                        </p>
                        {property.notes && (
                          <p className="text-xs text-slate-500 mt-2 italic">{property.notes}</p>
                        )}
                      </div>
                    </div>
                    {(propertyProspects.length > 0 || propertyQuotes.length > 0) && (
                      <div className="flex gap-2 mt-3">
                        {propertyProspects.length > 0 && (
                          <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium">
                            {propertyProspects.length} prospect{propertyProspects.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {propertyQuotes.length > 0 && (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                            {propertyQuotes.length} devis
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                  <div className="px-4 pb-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProperty(property.id);
                      }}
                      className="text-xs text-slate-400 hover:text-red-500 transition flex items-center gap-1"
                    >
                      <Trash2 size={12}/>
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Historique des prospects */}
      {prospects.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-purple-600"/>
            Historique des projets
          </h3>
          <div className="space-y-3">
            {prospects.map(prospect => (
              <div key={prospect.id} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{prospect.notes || 'Prospect'}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {prospect.address} - {prospect.city}
                  </p>
                </div>
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                  prospect.status === 'quoted' ? 'bg-green-100 text-green-700' :
                  prospect.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {prospect.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- VUE DÉTAIL D'UN BIEN AVEC HISTORIQUE ET ACTIONS ---
const PropertyDetailView = ({ property, prospects, quotes, activeCompanyId, onBack, onCreateQuote }) => {
  const typeEmoji = {
    maison: '🏠',
    appartement: '🏢',
    immeuble: '🏬',
    local: '🏪',
    terrain: '🌳'
  };

  const handleCreateDocument = (type) => {
    // Préparer les données du bien pour pré-remplir le document
    const propertyData = {
      type: type, // 'quote' ou 'invoice'
      clientName: property.clientName,
      clientId: property.clientId,
      propertyId: property.id,
      propertyAddress: property.address,
      propertyCity: property.city,
      propertyPostalCode: property.postalCode,
      propertyType: property.type,
      notes: property.notes || ''
    };
    
    if (onCreateQuote) {
      onCreateQuote(propertyData);
    }
  };

  return (
    <div className="animate-page">
      {/* Header avec gradient */}
      <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 mb-6 overflow-hidden">
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm -z-10"></div>
        <div className="text-white relative z-10">
          <button 
            onClick={onBack}
            className="text-blue-100 hover:text-white flex items-center gap-2 text-sm font-medium mb-4 transition"
          >
            <ArrowRight size={16} className="rotate-180"/> Retour au dossier
          </button>

          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{typeEmoji[property.type] || '🏠'}</span>
                <h1 className="text-3xl font-bold">{property.address}</h1>
              </div>
              <p className="text-blue-100 text-lg mb-3">
                {property.postalCode} {property.city}
              </p>
              <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-lg">
                <User size={14}/>
                <span className="text-xs font-medium">Propriétaire : {property.clientName}</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <div className="bg-white/20 px-4 py-2 rounded-lg text-center">
                <div className="text-2xl font-bold">{prospects.length}</div>
                <div className="text-xs">Projet{prospects.length > 1 ? 's' : ''}</div>
              </div>
              <div className="bg-white/20 px-4 py-2 rounded-lg text-center">
                <div className="text-2xl font-bold">{quotes.length}</div>
                <div className="text-xs">Devis</div>
              </div>
            </div>
          </div>

          {property.notes && (
            <div className="bg-white/10 rounded-lg p-3 mt-4">
              <p className="text-sm text-blue-50">{property.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Button 
          variant="primary" 
          onClick={() => handleCreateDocument('quote')}
          icon={FileText}
          className="w-full bg-indigo-600 hover:bg-indigo-700"
        >
          Nouveau Devis
        </Button>
        <Button 
          variant="primary" 
          onClick={() => handleCreateDocument('invoice')}
          icon={FileText}
          className="w-full bg-green-600 hover:bg-green-700"
        >
          Nouvelle Facture
        </Button>
      </div>

      {/* Historique des chantiers */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Calendar size={20} className="text-indigo-600"/>
          Historique des chantiers pour ce bien
        </h3>

        {quotes.length === 0 && prospects.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-xl">
            <FileText size={48} className="mx-auto text-slate-300 mb-3"/>
            <p className="text-slate-500 font-medium">Aucun chantier enregistré</p>
            <p className="text-slate-400 text-sm mt-1">Créez votre premier devis pour ce bien</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Afficher les devis/factures */}
            {quotes.map(quote => {
              const statusConfig = {
                draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
                sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
                accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700' },
                rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
                invoiced: { label: 'Facturé', color: 'bg-purple-100 text-purple-700' },
                paid: { label: 'Payé', color: 'bg-green-100 text-green-700' }
              };
              const status = statusConfig[quote.status] || statusConfig.draft;

              return (
                <div key={quote.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={16} className="text-indigo-600"/>
                        <h4 className="font-bold text-slate-800">
                          {quote.type === 'invoice' ? 'Facture' : 'Devis'} #{quote.number || 'N/A'}
                        </h4>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        {quote.date ? new Date(quote.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                      </p>
                      {quote.items && quote.items.length > 0 && (
                        <div className="text-xs text-slate-500">
                          {quote.items.filter(i => !i.isNote).length} ligne{quote.items.filter(i => !i.isNote).length > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-medium px-3 py-1 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                      {quote.totalTTC && (
                        <p className="text-lg font-bold text-slate-800 mt-2">
                          {parseFloat(quote.totalTTC).toFixed(2)} €
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Afficher les prospects non encore devisés */}
            {prospects.filter(p => !quotes.some(q => q.prospectId === p.id)).map(prospect => (
              <div key={prospect.id} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar size={16} className="text-purple-600"/>
                      <h4 className="font-bold text-slate-800">Prospect</h4>
                    </div>
                    <p className="text-sm text-slate-600">{prospect.notes || 'Projet en cours'}</p>
                    {prospect.date && (
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(prospect.date).toLocaleDateString('fr-FR')}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                    prospect.status === 'quoted' ? 'bg-green-100 text-green-700' :
                    prospect.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {prospect.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- ANCIENNE VUE DÉTAIL (gardée pour référence) ---
const PropertyDetail = ({ property, activeCompanyId, clients, prospects, quotes, onBack }) => {
  const [data, setData] = useState(property);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(property);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'companies', activeCompanyId, 'properties', property.id),
      (doc) => {
        if (doc.exists()) {
          setData({ id: doc.id, ...doc.data() });
          setEditForm({ id: doc.id, ...doc.data() });
        }
      }
    );
    return () => unsubscribe();
  }, [property.id, activeCompanyId]);

  const updateProperty = async (updates) => {
    try {
      await updateDoc(doc(db, 'companies', activeCompanyId, 'properties', property.id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur mise à jour:', error);
    }
  };

  const handleSaveEdit = async () => {
    await updateProperty(editForm);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement ce bien ?')) return;
    
    try {
      await deleteDoc(doc(db, 'companies', activeCompanyId, 'properties', property.id));
      onBack();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const client = clients.find(c => c.id === data.clientId);
  const relatedProspects = prospects.filter(p => p.propertyId === data.id);
  const relatedQuotes = quotes.filter(q => q.propertyId === data.id);

  const typeEmoji = {
    maison: '🏠',
    appartement: '🏢',
    immeuble: '🏬',
    local: '🏪',
    terrain: '🌳'
  };

  return (
    <div className="animate-page">
      {/* Header avec gradient */}
      <div className="relative bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 mb-6 overflow-hidden">
        <div className="absolute inset-0 bg-black/10 backdrop-blur-sm -z-10"></div>
        <div className="text-white relative z-10">
          <button 
            onClick={onBack}
            className="text-indigo-100 hover:text-white flex items-center gap-2 text-sm font-medium mb-4 transition"
          >
            <ArrowRight size={16} className="rotate-180"/> Retour aux biens
          </button>

          {!isEditing ? (
            <>
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{typeEmoji[data.type] || '🏠'}</span>
                    <h1 className="text-3xl font-bold">{data.address}</h1>
                  </div>
                  <p className="text-indigo-100 text-lg mb-3">
                    {data.postalCode} {data.city}
                  </p>
                  {client && (
                    <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-lg">
                      <User size={14}/>
                      <span className="text-xs font-medium">Propriétaire : {client.name}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition"
                  >
                    <Edit3 size={18}/>
                  </button>
                  <button 
                    onClick={handleDelete}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-xl transition"
                  >
                    <Trash2 size={18}/>
                  </button>
                </div>
              </div>

              {data.notes && (
                <div className="bg-white/10 rounded-lg p-3 mt-4">
                  <p className="text-sm text-indigo-50">{data.notes}</p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-indigo-100 mb-1">Adresse</label>
                <input 
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 text-white rounded-lg outline-none placeholder-indigo-200"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-indigo-100 mb-1">Code postal</label>
                  <input 
                    type="text"
                    value={editForm.postalCode}
                    onChange={(e) => setEditForm({...editForm, postalCode: e.target.value})}
                    className="w-full px-3 py-2 bg-white/20 text-white rounded-lg outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-indigo-100 mb-1">Ville</label>
                  <input 
                    type="text"
                    value={editForm.city}
                    onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                    className="w-full px-3 py-2 bg-white/20 text-white rounded-lg outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-indigo-100 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="w-full px-3 py-2 bg-white/20 text-white rounded-lg outline-none resize-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="flex-1 px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition"
                >
                  <Save size={16} className="inline mr-2"/>
                  Enregistrer
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition"
                >
                  <X size={16}/>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Historique des projets */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <FileText size={20} className="text-indigo-600"/>
          Historique des projets
        </h3>

        {relatedProspects.length === 0 && relatedQuotes.length === 0 ? (
          <p className="text-slate-500 text-sm italic">Aucun projet associé à ce bien pour le moment.</p>
        ) : (
          <div className="space-y-3">
            {relatedProspects.map(prospect => (
              <div key={prospect.id} className="flex items-center justify-between p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <div>
                  <p className="font-medium text-slate-800">Prospect</p>
                  <p className="text-sm text-slate-600">{prospect.notes}</p>
                </div>
                <span className="text-xs text-purple-600 font-medium">{prospect.status}</span>
              </div>
            ))}
            {relatedQuotes.map(quote => (
              <div key={quote.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div>
                  <p className="font-medium text-slate-800">Devis {quote.number}</p>
                  <p className="text-sm text-slate-600">{quote.date}</p>
                </div>
                <span className="text-xs text-blue-600 font-medium">{quote.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Properties;
